import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import useProviderProfile from "../hooks/useProviderProfile";
import "../styles/prescriptions/createPrescription.css";
import { SERVER_URL } from "../config";

const CreatePrescription: React.FC = () => {
  const { provider } = useProviderProfile();
  const clinicId = provider?.clinic?.id;
  const providerUserId = provider?.id;

  const [formData, setFormData] = useState({
    patientId: "",
    medicationId: "",
    dosage: "",
    quantity: 1,
    refills: 5,
    instructions: "",
    status: "APPROVED",
  });

  const [patients, setPatients] = useState<any[]>([]);
  const [medications, setMedications] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [patientsRes, medsRes] = await Promise.all([
          axios.get(`${SERVER_URL}/api/user/patients`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
          axios.get(`${SERVER_URL}/api/medication/meds`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
        ]);
        setPatients(patientsRes.data);
        setMedications(medsRes.data);
      } catch (err) {
        toast.error("Failed to load data.");
      }
    };

    fetchData();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "quantity" || name === "refills" ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(
        `${SERVER_URL}/api/prescription/create`,
        {
          ...formData,
          clinicId,
          providerUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Prescription created.");
      setFormData({
        patientId: "",
        medicationId: "",
        dosage: "",
        quantity: 1,
        refills: 5,
        instructions: "",
        status: "REQUESTED",
      });
    } catch (err: any) {
      console.error("Error:", err);
      toast.error(err.response?.data?.error || "Error creating prescription.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="create-rx-form">
      <h2 className="create-rx-title">Create Prescription</h2>

      <select
        name="patientId"
        value={formData.patientId}
        onChange={handleChange}
        required
        className="create-rx-select"
      >
        <option value="">Select Patient</option>
        {patients.map((p) => (
          <option key={p.id} value={p.id}>
            {p.patientProfile?.firstName} {p.patientProfile?.lastName}
          </option>
        ))}
      </select>

      <select
        name="medicationId"
        value={formData.medicationId}
        onChange={handleChange}
        required
        className="create-rx-select"
      >
        <option value="">Select Medication</option>
        {medications.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>

      <input
        name="dosage"
        value={formData.dosage}
        onChange={handleChange}
        placeholder="Dosage"
        required
        className="create-rx-input"
      />

      <input
        name="quantity"
        type="number"
        value={formData.quantity}
        onChange={handleChange}
        min={1}
        className="create-rx-input"
      />

      <input
        name="refills"
        type="number"
        value={formData.refills}
        onChange={handleChange}
        min={0}
        className="create-rx-input"
      />

      <input
        name="instructions"
        value={formData.instructions}
        onChange={handleChange}
        placeholder="Instructions"
        className="create-rx-input"
      />

      <button type="submit" className="primary-button">
        Create Prescription
      </button>
    </form>
  );
};

export default CreatePrescription;
